import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, LogIn, AlertCircle, Copy, Users, Loader2, LogOut, CheckCircle } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { cn } from '../../../utils/cn';
import { getDeviceId } from '../../../utils/deviceId';
import { CATEGORY_CONFIGS, StockCategoryFilter } from '../../../types/stockCategories';
import { useLeaderboard, LeaderboardEntry } from '../../../hooks/useLeaderboard';
import { PercentileRank } from '../../game/PercentileRank';
import { useAuth } from '../../../hooks/useAuth';
import { usePrivateLeaderboards } from '../../../hooks/usePrivateLeaderboards';
import { PrivateLeaderboardRanking } from '../../../types/api';
import { CreateLeaderboardModal } from './CreateLeaderboardModal';
import { JoinLeaderboardModal } from './JoinLeaderboardModal';

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: StockCategoryFilter;
  gameDate?: string;
  onSignInClick?: () => void;
}

const formatTime = (timeMs: number): string => {
  const totalSeconds = Math.floor(timeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const normalizeTrackedCategories = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter((value): value is string => typeof value === 'string');
  }

  if (typeof raw === 'string') {
    return [raw];
  }

  if (raw && typeof raw === 'object') {
    return Object.values(raw as Record<string, unknown>).flatMap(value =>
      normalizeTrackedCategories(value)
    );
  }

  return [];
};

const LeaderboardRow: React.FC<{
  entry: LeaderboardEntry;
  highlight?: boolean;
}> = ({ entry, highlight = false }) => {
  const isFailure = !entry.won;
  const achievementIconUrl = entry.equipped_achievement_icon_url;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg transition-colors',
        highlight
          ? 'bg-blue-100 dark:bg-blue-900/30 font-semibold'
          : isFailure
            ? 'bg-gray-50/50 dark:bg-gray-800/30'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      {achievementIconUrl && (
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `url(${achievementIconUrl})`,
            backgroundRepeat: 'repeat-x',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
      )}
      <div className="relative flex items-center justify-between py-2 px-2 sm:px-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          <span
            className={cn(
              "text-sm sm:text-lg font-bold shrink-0",
              isFailure ? "text-gray-300 dark:text-gray-600" : "text-gray-400"
            )}
          >
            #{entry.rank}
          </span>
          <span
            className={cn(
              "text-sm sm:text-base flex-1 truncate",
              isFailure && "italic text-gray-500 dark:text-gray-500"
            )}
          >
            {entry.username}
            {isFailure && (
              <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                DNF
              </span>
            )}
          </span>
        </div>
        <div
          className={cn(
            "flex items-center gap-2 sm:gap-6 text-xs sm:text-sm shrink-0",
            isFailure
              ? "text-gray-400 dark:text-gray-600"
              : "text-gray-600 dark:text-gray-400"
          )}
        >
          <span className="text-center whitespace-nowrap">
            {entry.guesses} <span className="hidden sm:inline">{entry.guesses === 1 ? 'guess' : 'guesses'}</span>
          </span>
          <span className="text-right font-mono whitespace-nowrap">{formatTime(entry.time_ms)}</span>
        </div>
      </div>
    </div>
  );
};

export const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  isOpen,
  onClose,
  category,
  gameDate,
  onSignInClick
}) => {
  const { user } = useAuth();
  const categoryConfig = CATEGORY_CONFIGS[category] || CATEGORY_CONFIGS['popular'];

  // Private leaderboard state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedPrivateGroup, setSelectedPrivateGroup] = useState<string | null>(null);
  const [privateRankings, setPrivateRankings] = useState<PrivateLeaderboardRanking[]>([]);
  const [loadingPrivate, setLoadingPrivate] = useState(false);
  const [privateError, setPrivateError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<{ groupId: string; type: 'code' | 'link' } | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [leavingGroup, setLeavingGroup] = useState(false);
  const [leaveSuccessMessage, setLeaveSuccessMessage] = useState<string | null>(null);

  // Public leaderboard refresh feedback
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);
  const refreshTriggerRef = React.useRef(false);

  const {
    userGroups,
    fetchUserGroups,
    createLeaderboard: createPrivateLeaderboard,
    joinLeaderboard: joinPrivateLeaderboard,
    leaveLeaderboard: leavePrivateLeaderboard,
    getGroupRankings,
    getInviteLink,
    loading: privateApiLoading,
    error: privateApiError
  } = usePrivateLeaderboards();

  const { top5, currentUserRank, currentUserEntry, isLoading, error, refetch } = useLeaderboard({
    category,
    gameDate,
    userId: user?.id,
    deviceId: !user ? getDeviceId() : null,
    autoFetch: isOpen
  });

  // Fetch user's private leaderboards when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchUserGroups();
    }
  }, [isOpen, user, fetchUserGroups]);

  // Detect when public leaderboard refresh completes successfully
  useEffect(() => {
    if (refreshTriggerRef.current && !isLoading && !error) {
      // Refresh completed successfully
      setShowRefreshSuccess(true);
      refreshTriggerRef.current = false;

      // Clear success indicator after 2 seconds
      const timer = setTimeout(() => {
        setShowRefreshSuccess(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error]);

  // Auto-select the first leaderboard when data is available
  useEffect(() => {
    if (userGroups.length === 0) {
      setSelectedPrivateGroup(null);
      setPrivateRankings([]);
      setInviteLink(null);
      setPrivateError(null);
      return;
    }

    const hasSelectedGroup = selectedPrivateGroup
      ? userGroups.some(group => group.group_id === selectedPrivateGroup)
      : false;

    if (!hasSelectedGroup) {
      setSelectedPrivateGroup(userGroups[0].group_id);
    }
  }, [userGroups, selectedPrivateGroup]);

  const loadPrivateRankings = useCallback(async (groupId: string) => {
    try {
      setLoadingPrivate(true);
      setPrivateError(null);
      const rankings = await getGroupRankings(groupId, category, gameDate);
      setPrivateRankings(rankings);
    } catch (err) {
      console.error('Error loading private leaderboard rankings:', err);
      setPrivateError('Unable to load rankings for this leaderboard.');
    } finally {
      setLoadingPrivate(false);
    }
  }, [category, gameDate, getGroupRankings]);

  // Load private leaderboard rankings when a group is selected (only on group change or modal open)
  useEffect(() => {
    if (selectedPrivateGroup && user && isOpen) {
      loadPrivateRankings(selectedPrivateGroup);
    }
  }, [selectedPrivateGroup, user, isOpen, loadPrivateRankings]);

  useEffect(() => {
    setCopyFeedback(null);
  }, [selectedPrivateGroup]);

  // Fetch invite link details for selected group
  useEffect(() => {
    if (!selectedPrivateGroup || !user) {
      setInviteLink(null);
      return;
    }

    let isMounted = true;

    getInviteLink(selectedPrivateGroup)
      .then(result => {
        if (isMounted) {
          setInviteLink(result?.invite_link || null);
        }
      })
      .catch(err => {
        console.error('Error fetching invite link for private leaderboard:', err);
        if (isMounted) {
          setInviteLink(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPrivateGroup, user, getInviteLink]);

  const displayDate = useMemo(() => {
    const date = gameDate ? new Date(gameDate) : new Date();
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }, [gameDate]);

  const hasUserPlayed = currentUserRank !== null || top5.some(e => e.is_current_user);
  const showEllipsis = currentUserRank && currentUserRank.rank > 5;

  const selectedGroup = useMemo(() => {
    return userGroups.find(group => group.group_id === selectedPrivateGroup) || null;
  }, [userGroups, selectedPrivateGroup]);

  const selectedGroupCategories = useMemo(() => {
    return selectedGroup ? normalizeTrackedCategories(selectedGroup.tracked_categories) : [];
  }, [selectedGroup]);

  const memberCount = selectedGroup ? Number(selectedGroup.member_count ?? 0) : 0;
  const maxMembers = selectedGroup ? Number(selectedGroup.max_members ?? 0) : 0;

  const handleRefreshPrivate = useCallback(() => {
    if (selectedPrivateGroup) {
      loadPrivateRankings(selectedPrivateGroup);
    }
  }, [selectedPrivateGroup, loadPrivateRankings]);

  const handleCopyValue = useCallback(async (value: string, groupId: string, type: 'code' | 'link') => {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        console.warn('Clipboard API not available in this environment');
        return;
      }

      await navigator.clipboard.writeText(value);
      setCopyFeedback({ groupId, type });
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      console.error('Failed to copy private leaderboard data to clipboard:', err);
    }
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    const config = CATEGORY_CONFIGS[cat as StockCategoryFilter];
    return config ? config.label : cat.toUpperCase();
  }, []);

  const formatDateLabel = useCallback((value: string | null | undefined) => {
    if (!value) {
      return 'Unknown date';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown date';
    }

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  const handleLeaveLeaderboard = useCallback(async () => {
    if (!selectedPrivateGroup) return;

    setLeavingGroup(true);
    setPrivateError(null);
    setLeaveSuccessMessage(null);

    try {
      const result = await leavePrivateLeaderboard(selectedPrivateGroup);

      if (result.success) {
        setLeaveSuccessMessage(result.message || 'Successfully left the leaderboard');
        setShowLeaveConfirmation(false);

        // Select the next available group or clear selection
        const remainingGroups = userGroups.filter(g => g.group_id !== selectedPrivateGroup);
        if (remainingGroups.length > 0) {
          setSelectedPrivateGroup(remainingGroups[0].group_id);
        } else {
          setSelectedPrivateGroup(null);
          setPrivateRankings([]);
        }

        // Clear success message after 5 seconds
        setTimeout(() => setLeaveSuccessMessage(null), 5000);
      } else {
        setPrivateError(result.error || 'Failed to leave leaderboard');
      }
    } catch (err) {
      console.error('Error leaving leaderboard:', err);
      setPrivateError('An unexpected error occurred while leaving the leaderboard');
    } finally {
      setLeavingGroup(false);
    }
  }, [selectedPrivateGroup, leavePrivateLeaderboard, userGroups]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${categoryConfig.icon} ${categoryConfig.label} Leaderboard`}
      description={`Daily leaderboard for ${categoryConfig.label.toLowerCase()}`}
      className="max-w-[calc(100vw-1rem)] sm:max-w-md md:max-w-lg lg:max-w-2xl max-h-[85vh] sm:max-h-[90vh]"
    >
      <div>
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold">Public Leaderboard</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refreshTriggerRef.current = true;
                void refetch();
              }}
              disabled={isLoading || showRefreshSuccess}
              className={cn(
                "h-8 px-3 gap-1.5 !shadow-none hover:!shadow-sm transition-colors",
                showRefreshSuccess && "border-green-500 dark:border-green-600 text-green-600 dark:text-green-400"
              )}
            >
              {showRefreshSuccess ? (
                <>
                  <CheckCircle className="h-2.5 w-2.5" />
                  Updated!
                </>
              ) : (
                <>
                  <RefreshCw className={cn("h-2.5 w-2.5", isLoading && "animate-spin")} />
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </>
              )}
            </Button>
          </div>
          <div className="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between">
            <p>
              Daily Global Public Leaderboard — {displayDate}
            </p>
            {currentUserRank && (
              <p>
                You're currently ranked #{currentUserRank.rank}
              </p>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div>
          {/* Loading */}
          {isLoading && !top5.length && (
            <div className="space-y-1">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              ))}
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="text-center py-8 text-red-500">
              Failed to load: {error}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && top5.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                🏆 Be the first to post a score today!
              </p>
              <p className="text-sm text-gray-500">
                Complete today's puzzle to claim the top spot.
              </p>
            </div>
          )}

          {/* Leaderboard */}
          {!isLoading && !error && top5.length > 0 && (
            <div className="space-y-1">
              {/* Top 5 */}
              {top5.map(entry => (
                <LeaderboardRow
                  key={entry.rank}
                  entry={entry}
                  highlight={entry.is_current_user}
                />
              ))}

              {/* Ellipsis + User Row */}
              {showEllipsis && currentUserRank && (
                <>
                  <div className="text-center py-1 text-gray-400 text-xl">…</div>
                  <LeaderboardRow entry={currentUserRank} highlight />
                </>
              )}

              {/* No Play Message */}
              {!hasUserPlayed && (
                <div className="mt-4 text-center py-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Play today's puzzle to get ranked!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* User Percentile Display */}
          {!isLoading && !error && hasUserPlayed && currentUserEntry?.percentile && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <PercentileRank
                percentile={currentUserEntry.percentile}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Private Leaderboards Section */}
          {user && (
            <>
              <div className="mt-8 pt-6 border-t-2 border-gray-300 dark:border-gray-600">
              <h3 className="text-xl font-bold mb-4">Private Leaderboards</h3>

              {/* Create/Join Buttons */}
              <div className="flex gap-3 mb-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(true)}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowJoinModal(true)}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Join
                </Button>
              </div>

              {privateApiError && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{privateApiError}</span>
                </div>
              )}

              {/* User's Private Leaderboards */}
              {userGroups.length > 0 && (() => {
                const groupsTrackingCategory = userGroups.filter(group => {
                  const categories = normalizeTrackedCategories(group.tracked_categories);
                  return categories.length === 0 || categories.includes(category);
                });

                return (
                  <div className="flex flex-col gap-6">
                    {groupsTrackingCategory.length === 0 && (
                      <div className="text-center py-6 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        None of your private leaderboards track the {getCategoryLabel(category)} category
                      </div>
                    )}
                    <div className="grid gap-3">
                      {userGroups.map(group => {
                        const isSelected = group.group_id === selectedPrivateGroup;
                        const groupMemberCount = Number(group.member_count ?? 0);
                        const groupMaxMembers = Number(group.max_members ?? 0);
                        const categories = normalizeTrackedCategories(group.tracked_categories);
                        const tracksCurrentCategory = categories.length === 0 || categories.includes(category);

                      return (
                        <button
                          key={group.group_id}
                          type="button"
                          onClick={() => tracksCurrentCategory && setSelectedPrivateGroup(group.group_id)}
                          disabled={!tracksCurrentCategory}
                          className={cn(
                            'w-full text-left rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 transition-all',
                            'hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                            isSelected && 'border-blue-500 shadow-lg bg-blue-50 dark:bg-blue-900/20',
                            !tracksCurrentCategory && 'opacity-50 cursor-not-allowed hover:shadow-none hover:border-gray-300 dark:hover:border-gray-700'
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className="text-lg font-semibold flex items-center gap-2">
                                {group.group_name}
                                {group.is_creator && (
                                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                                    Creator
                                  </span>
                                )}
                              </h4>
                              {group.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                  {group.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                              <Users className="w-4 h-4" />
                              <span>
                                {groupMemberCount}
                                {groupMaxMembers ? ` / ${groupMaxMembers}` : ''}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 flex items-end justify-between gap-3">
                            {categories.length > 0 ? (
                              <div className="flex flex-wrap gap-2 flex-1">
                                {categories.slice(0, 3).map(categoryKey => (
                                  <span
                                    key={`${group.group_id}-${categoryKey}`}
                                    className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200"
                                  >
                                    {getCategoryLabel(categoryKey)}
                                  </span>
                                ))}
                                {categories.length > 3 && (
                                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    +{categories.length - 3}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                                Tracking default categories
                              </p>
                            )}
                            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              Joined {formatDateLabel(group.joined_at)}
                            </div>
                          </div>

                          {!tracksCurrentCategory && (
                            <div className="mt-2 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded">
                              Not tracking {getCategoryLabel(category)} category
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {selectedGroup && (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4 shadow-sm">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold flex items-center gap-2">
                              {selectedGroup.group_name}
                              {selectedGroup.is_creator && (
                                <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                                  Creator
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {memberCount} {memberCount === 1 ? 'member' : 'members'}
                              {maxMembers ? ` • Max ${maxMembers}` : ''}
                            </p>
                            {selectedGroup.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                                {selectedGroup.description}
                              </p>
                            )}
                            {selectedGroupCategories.length > 0 ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {selectedGroupCategories.slice(0, 3).map(categoryKey => (
                                  <span
                                    key={`${selectedGroup.group_id}-badge-${categoryKey}`}
                                    className="text-xs font-medium px-2 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                                  >
                                    {getCategoryLabel(categoryKey)}
                                  </span>
                                ))}
                                {selectedGroupCategories.length > 3 && (
                                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    +{selectedGroupCategories.length - 3}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                Tracking default categories
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 sm:w-60">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleRefreshPrivate}
                              disabled={loadingPrivate}
                              className="w-full gap-1.5 !shadow-none hover:!shadow-sm"
                            >
                              <RefreshCw className={cn("h-2.5 w-2.5", loadingPrivate && "animate-spin")} />
                              {loadingPrivate ? 'Refreshing...' : 'Refresh'}
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowLeaveConfirmation(true)}
                              disabled={leavingGroup || privateApiLoading}
                              className="w-full gap-1.5 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 !shadow-none hover:!shadow-sm"
                            >
                              <LogOut className="h-2.5 w-2.5" />
                              {leavingGroup ? 'Leaving...' : 'Leave Leaderboard'}
                            </Button>

                            <div className="flex items-center gap-2">
                              <span className="flex-1 px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-900/40 font-mono text-xs text-center">
                                {selectedGroup.invite_code}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="gap-1"
                                onClick={() => handleCopyValue(selectedGroup.invite_code, selectedGroup.group_id, 'code')}
                              >
                                <Copy className="w-4 h-4" />
                                {copyFeedback?.groupId === selectedGroup.group_id && copyFeedback?.type === 'code' ? 'Copied' : 'Copy'}
                              </Button>
                            </div>

                            {inviteLink && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="gap-1"
                                onClick={() => handleCopyValue(inviteLink, selectedGroup.group_id, 'link')}
                              >
                                <Copy className="w-4 h-4" />
                                {copyFeedback?.groupId === selectedGroup.group_id && copyFeedback?.type === 'link' ? 'Link Copied' : 'Copy Invite Link'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {privateError && (
                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                          <AlertCircle className="w-4 h-4" />
                          {privateError}
                        </div>
                      )}

                      {leaveSuccessMessage && (
                        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-600 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300">
                          <CheckCircle className="w-4 h-4" />
                          {leaveSuccessMessage}
                        </div>
                      )}

                      <div className="space-y-2">
                        {loadingPrivate && (
                          <div className="space-y-2">
                            {[1, 2, 3].map(i => (
                              <div key={`private-skeleton-${i}`} className="h-14 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                            ))}
                          </div>
                        )}

                        {!loadingPrivate && !privateError && privateRankings.length === 0 && (
                          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No rankings yet for this leaderboard.
                          </div>
                        )}

                        {!loadingPrivate && !privateError && privateRankings.length > 0 && (
                          <>
                            {privateRankings.map(entry => (
                              <LeaderboardRow
                                key={`private-entry-${entry.rank}-${entry.user_id}`}
                                entry={{
                                  rank: entry.rank,
                                  username: entry.username,
                                  guesses: entry.guesses,
                                  time_ms: entry.time_ms,
                                  is_current_user: entry.is_current_user,
                                  percentile: '?', // Private leaderboards don't show percentile
                                  total_players: undefined,
                                  won: entry.won,  // Include won status from backend
                                  equipped_achievement_icon_url: entry.equipped_achievement_icon_url,
                                }}
                                highlight={entry.is_current_user}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                );
              })()}

              {userGroups.length === 0 && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  {privateApiLoading ? (
                    <div className="flex flex-col items-center gap-2 text-sm">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span>Loading your private leaderboards…</span>
                    </div>
                  ) : (
                    <>
                      <p className="mb-2">You haven't joined any private leaderboards yet.</p>
                      <p className="text-sm">Create one or join using an invite code!</p>
                    </>
                  )}
                </div>
              )}
              </div>
            </>
          )}

          {/* Private Leaderboards Sign-in Prompt for Guests */}
          {!user && (
            <div className="mt-8 pt-6 border-t-2 border-gray-300 dark:border-gray-600">
              <h3 className="text-xl font-bold mb-4">Private Leaderboards</h3>
              <div className="py-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                  {onSignInClick ? (
                    <>
                      <button
                        onClick={() => {
                          onClose();
                          onSignInClick();
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        Sign in
                      </button>
                      {' '}to <strong>create or join</strong> private leaderboards
                    </>
                  ) : (
                    <>Sign in to <strong>create or join</strong> private leaderboards</>
                  )}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Create a free account to compete on the leaderboard <strong>with friends</strong> and track your rank
                </p>
              </div>
            </div>
          )}
        </div>
        {/* End Content Area */}

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={onClose}
            className="w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </Button>
        </div>
      </div>

      {/* Create/Join Modals */}
      <CreateLeaderboardModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        createLeaderboard={createPrivateLeaderboard}
        isProcessing={privateApiLoading}
        userGroupCount={userGroups.length}
        onSuccess={(groupId) => {
          if (groupId) {
            setSelectedPrivateGroup(groupId);
          }
          fetchUserGroups();
        }}
      />

      <JoinLeaderboardModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        joinLeaderboard={joinPrivateLeaderboard}
        isProcessing={privateApiLoading}
        userGroupCount={userGroups.length}
        onSuccess={(groupId) => {
          if (groupId) {
            setSelectedPrivateGroup(groupId);
          }
          fetchUserGroups();
        }}
      />

      {/* Leave Confirmation Dialog */}
      {selectedGroup && (
        <div className="relative z-[60]">
          <Modal
            isOpen={showLeaveConfirmation}
            onClose={() => !leavingGroup && setShowLeaveConfirmation(false)}
            title="Leave Leaderboard"
            description="Confirm leaving this private leaderboard"
            className="max-w-md"
          >
          <div className="p-6">
            <div className="mb-6">
              <p className="text-base text-gray-700 dark:text-gray-300">
                Are you sure you want to leave <strong>{selectedGroup.group_name}</strong>?
              </p>
            </div>

            {privateError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                <AlertCircle className="w-4 h-4" />
                {privateError}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setShowLeaveConfirmation(false)}
                disabled={leavingGroup}
                className="flex-1 py-2 px-4 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLeaveLeaderboard}
                disabled={leavingGroup}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {leavingGroup ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Leaving...
                  </>
                ) : (
                  'Leave Leaderboard'
                )}
              </Button>
            </div>
          </div>
        </Modal>
        </div>
      )}
    </Modal>
  );
};

export default LeaderboardModal;
